import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ExternalLink, Map, MapPin, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { individualLocatedOffers, interleaveOffers } from "@/lib/liveListingMap";
import { supabase } from "@/integrations/supabase/client";
import { supportsLiveListings } from "@/lib/portalCrawlability";
import { pexelsSrc, pexelsSrcSet } from "@/lib/pexelsImage";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || "";

interface PexelsImage {
  src: string;
  alt: string;
  photographer: string;
  photographerUrl: string;
}

interface ListingLocation {
  name: string;
  address?: string;
  precision?: string;
  coordinates?: [number, number];
}

interface Listing {
  title: string;
  link: string;
  type: string;
  typeLabel: string;
  bullets: string[];
  image: PexelsImage | null;
  location?: ListingLocation | null;
}

interface AggregatedListing extends Listing {
  platform: string;
}

interface PortalRef {
  name: string;
  url: string;
}

interface LiveListingsExplorerProps {
  /** Restrict to a city/region; omit for a nationwide view */
  city?: string;
  title?: string;
  portals: PortalRef[];
  /** How many crawlable portals to aggregate (ordered by crawl priority) */
  maxPortals?: number;
  mapCenter: [number, number];
  mapZoom?: number;
  mapInitiallyOpen?: boolean;
  /** Notified with the platform names that match the active location filter (null = no filter) */
  onMatchingPlatforms?: (names: string[] | null) => void;
}

const normalize = (s: string) =>
  s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const LiveListingsExplorer = ({
  city,
  title,
  portals,
  maxPortals = 6,
  mapCenter,
  mapZoom,
  mapInitiallyOpen = false,
  onMatchingPlatforms,
}: LiveListingsExplorerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const markers = useRef<any[]>([]);
  const autoAdvanceRef = useRef<number | null>(null);
  const pausedRef = useRef(false);

  const [inView, setInView] = useState(false);
  const [listings, setListings] = useState<AggregatedListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [api, setApi] = useState<CarouselApi>();
  const [mapboxgl, setMapboxgl] = useState<any>(null);
  const [mapOpen, setMapOpen] = useState(mapInitiallyOpen);

  const headline = title ?? `Neueste Gewerbeimmobilien-Angebote in ${city}`;

  // Only start loading when the section scrolls into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && setInView(true),
      { rootMargin: "1200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    const cacheKey = `live-offers:${city ?? "de"}:${portals.map((p) => p.url).join("|").length}:${maxPortals}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setListings(JSON.parse(cached)); setIsLoading(false); }
      else setIsLoading(true);
    } catch { setIsLoading(true); }

    const crawlable = portals.filter((p) => supportsLiveListings(p.url)).slice(0, maxPortals);

    (async () => {
      const results = await Promise.allSettled(
        crawlable.map(async (portal) => {
          const { data, error } = await supabase.functions.invoke("portal-carousel", {
            body: { platform: portal.name, url: portal.url, locationVersion: 2, ...(city ? { city } : {}) },
          });
          if (error || !data?.listings?.length) return [];
          return (data.listings as Listing[]).map((listing) => ({
            ...listing,
            platform: portal.name,
          }));
        }),
      );
      if (cancelled) return;

      const buckets = results
        .filter((r): r is PromiseFulfilledResult<AggregatedListing[]> => r.status === "fulfilled")
        .map((r) => r.value)
        .filter((bucket) => bucket.length > 0);

      // Interleave, then dedupe images so no stock photo appears twice
      const merged = interleaveOffers(buckets);
      const usedImages = new Set<string>();
      const unique: AggregatedListing[] = [];
      for (const item of merged) {
        if (item.image && usedImages.has(item.image.src)) item.image = null;
        if (item.image) usedImages.add(item.image.src);
        unique.push(item);
      }

      if (unique.length) {
        setListings(unique);
        try { localStorage.setItem(cacheKey, JSON.stringify(unique)); } catch { /* storage full */ }
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [inView, city, portals, maxPortals]);

  // Location filter (client-side, matches title, bullets, extracted location, platform)
  const filterTerm = normalize(filter.trim());
  const filterActive = filterTerm.length >= 2;
  const filtered = useMemo(() => {
    if (!filterActive) return listings;
    return listings.filter((l) =>
      normalize(
        `${l.title} ${l.bullets.join(" ")} ${l.location?.name ?? ""} ${l.platform}`,
      ).includes(filterTerm),
    );
  }, [listings, filterTerm, filterActive]);

  // Tell the page which platforms have matching listings so it can filter its grid
  const matchingKey = useMemo(() => {
    if (!filterActive) return "";
    return JSON.stringify(Array.from(new Set(filtered.map((l) => l.platform))).sort());
  }, [filterActive, filtered]);

  useEffect(() => {
    if (!onMatchingPlatforms) return;
    if (!filterActive) {
      onMatchingPlatforms(null);
      return;
    }
    onMatchingPlatforms(matchingKey ? (JSON.parse(matchingKey) as string[]) : []);
  }, [matchingKey, filterActive, onMatchingPlatforms]);

  // Slow auto-advance for the carousel
  useEffect(() => {
    if (!api) return;
    const tick = () => {
      if (!pausedRef.current) {
        if (api.canScrollNext()) api.scrollNext();
        else api.scrollTo(0);
      }
    };
    autoAdvanceRef.current = window.setInterval(tick, 4500);
    return () => {
      if (autoAdvanceRef.current) window.clearInterval(autoAdvanceRef.current);
    };
  }, [api, filtered.length]);

  // Lazy-load mapbox-gl JS + CSS once the component mounts
  useEffect(() => {
    let cancelled = false;
    if (!MAPBOX_TOKEN || !mapOpen) return;
    const linkId = "mapbox-gl-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = "https://api.mapbox.com/mapbox-gl-js/v3.4.0/mapbox-gl.css";
      document.head.appendChild(link);
    }
    import("mapbox-gl").then((mod) => {
      if (!cancelled) setMapboxgl(mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, [mapOpen]);

  const updateMarkers = useCallback(() => {
    if (!map.current || !mapboxgl) return;
    markers.current.forEach((m) => m.remove());
    markers.current = [];

    const points = individualLocatedOffers(filtered);
    points.forEach(({ offer: listing, offset }) => {
      const location = listing.location;
      if (!location?.coordinates) return;
      const popup = new mapboxgl.Popup({ maxWidth: "320px" }).setHTML(
        `<div class="p-3 bg-background text-foreground"><h3 class="font-bold mb-2">${escapeHtml(location.address ?? location.name)}</h3><p class="text-xs text-muted-foreground mb-2">${location.precision === "address" ? "Veröffentlichter Objektstandort" : "Ungefähre Lage im Stadtteil; keine genaue Objektposition"}</p><a href="${escapeHtml(listing.link)}" target="_blank" rel="noopener noreferrer" class="text-primary underline text-sm">${escapeHtml(listing.title)}</a><p class="text-xs text-muted-foreground">${escapeHtml(listing.platform)}</p></div>`,
      );
      const element = document.createElement("button");
      element.type = "button";
      element.className = "h-6 w-6 rounded-full border-2 border-background bg-primary shadow-md focus-visible:ring-2 focus-visible:ring-ring";
      element.setAttribute("data-offer-marker", "");
      element.setAttribute("aria-label", `${listing.title} – ${location.name}`);
      const marker = new mapboxgl.Marker({ element, offset })
        .setLngLat(location.coordinates)
        .setPopup(popup)
        .addTo(map.current);
      markers.current.push(marker);
    });

    const coordinates = points.flatMap(({ offer }) => offer.location?.coordinates ? [offer.location.coordinates] : []);
    if (coordinates.length > 1) {
      const lngs = coordinates.map((c) => c[0]);
      const lats = coordinates.map((c) => c[1]);
      map.current.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 80, maxZoom: city ? 12 : 6, duration: 800 },
      );
    } else if (coordinates.length === 1) {
      map.current.flyTo({ center: coordinates[0], zoom: 12 });
    }
  }, [filtered, mapboxgl, city]);

  useEffect(() => {
    if (!mapOpen || !mapContainer.current || !MAPBOX_TOKEN || !mapboxgl || map.current || isLoading) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: mapCenter,
      zoom: mapZoom ?? (city ? 9 : 5),
    });
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.current.on("load", () => {
      updateMarkers();
      const container = mapContainer.current;
      if (container) {
        container.querySelectorAll('a[href*="mapbox.com"]').forEach((link: Element) => {
          if (!link.textContent?.trim() && !link.getAttribute("aria-label")) {
            link.setAttribute("aria-label", "Mapbox");
          }
        });
      }
    });
    return () => {
      markers.current.forEach((m) => m.remove());
      map.current?.remove();
      map.current = null;
    };
  }, [mapOpen, mapboxgl, isLoading, mapCenter, mapZoom, city, updateMarkers]);

  // Re-draw markers when the filter changes
  useEffect(() => {
    if (map.current?.loaded()) updateMarkers();
  }, [filtered, updateMarkers]);

  if (!isLoading && listings.length === 0) return null;

  const platformCount = new Set(filtered.map((l) => l.platform)).size;
  const locatedCount = individualLocatedOffers(filtered).length;
  const showMap = MAPBOX_TOKEN && !isLoading && locatedCount > 0;

  return (
    <section ref={containerRef} className="mb-10" aria-label={headline}>
      <div className="flex items-center gap-2 mb-4">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <h2 className="text-xl md:text-2xl font-bold">{headline}</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Nach Ort oder Stadtteil filtern (z. B. Feuerbach)"
            className="pl-9 pr-9"
            aria-label="Angebote nach Ort filtern"
          />
          {filter && (
            <button
              type="button"
              aria-label="Filter zurücksetzen"
              onClick={() => setFilter("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {!isLoading && (
          <p className="text-sm text-muted-foreground">
            {filterActive
              ? `${filtered.length} Angebote für „${filter.trim()}“ von ${platformCount} ${platformCount === 1 ? "Portal" : "Portalen"}`
              : `${listings.length} Angebote von ${new Set(listings.map((l) => l.platform)).size} Portalen – täglich aktualisiert`}
          </p>
        )}
      </div>

      <Button variant="outline" className="mb-4 gap-2" onClick={() => setMapOpen((open) => !open)} aria-expanded={mapOpen} aria-controls="live-offers-map">
        <Map className="h-4 w-4" aria-hidden="true" />
        {mapOpen ? "Angebotskarte ausblenden" : "Angebotskarte anzeigen"}
      </Button>

      {isLoading ? (
        <div className="space-y-4">
          {mapOpen && <Skeleton className="w-full h-[380px] rounded-lg" />}
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full rounded-lg" />
            ))}
          </div>
        </div>
      ) : (
        <>
          {mapOpen && showMap && (
            <div id="live-offers-map" ref={mapContainer} className="w-full h-[380px] rounded-lg shadow-md mb-2" />
          )}
          {mapOpen && <p className="text-xs text-muted-foreground mb-6">{locatedCount} von {filtered.length} Angeboten mit Adresse oder Stadtteil auf der Karte. Stadtteilpunkte zeigen die ungefähre Lage; Punkte am selben Standort sind zur Auswahl leicht versetzt. Angebote ohne genauere Ortsangabe stehen unten.</p>}

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-muted/40 rounded-lg p-6 text-center">
              Aktuell keine Live-Angebote für „{filter.trim()}“ gefunden. Filter zurücksetzen, um
              alle Angebote zu sehen.
            </p>
          ) : (
            <div
              onMouseEnter={() => (pausedRef.current = true)}
              onMouseLeave={() => (pausedRef.current = false)}
            >
              <Carousel
                setApi={setApi}
                opts={{ align: "start", loop: true }}
                className="w-full"
                key={filtered.length}
              >
                <CarouselContent>
                  {filtered.map((listing, index) => (
                    <CarouselItem
                      key={`${listing.platform}-${index}`}
                      className="md:basis-1/2 lg:basis-1/3"
                    >
                      <article className="border rounded-lg overflow-hidden bg-card h-full flex flex-col">
                        <div className="relative aspect-[3/2] bg-muted">
                          {listing.image && (
                            <img
                              src={pexelsSrc(listing.image.src, 480)}
                              srcSet={pexelsSrcSet(listing.image.src)}
                              sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                              alt={listing.image.alt}
                              className="w-full h-full object-cover"
                              loading={index < 3 ? "eager" : "lazy"}
                              fetchPriority={index === 0 ? "high" : "auto"}
                              decoding="async"
                              width={480}
                              height={320}
                            />
                          )}
                          <div className="absolute bottom-0 inset-x-0 bg-black/50 px-2 py-1 flex items-center justify-between">
                            <a
                              href={listing.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-white/90 hover:underline"
                            >
                              Symbolbild – echte Bilder im Exposé
                            </a>
                            {listing.image && (
                              <a
                                href={listing.image.photographerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-white/80 hover:underline"
                              >
                                Foto: {listing.image.photographer} / Pexels
                              </a>
                            )}
                          </div>
                        </div>

                        <div className="p-4 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs font-normal">
                              {listing.platform}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {listing.typeLabel}
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-sm leading-snug line-clamp-2 mb-2">
                            {listing.title}
                          </h3>
                          <ul className="space-y-1 mb-3 flex-1">
                            {listing.bullets.slice(0, 3).map((bullet, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex gap-1.5">
                                {i === 0 ? (
                                  <MapPin
                                    className="h-3 w-3 text-primary shrink-0 mt-0.5"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <span
                                    className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-1.5"
                                    aria-hidden="true"
                                  />
                                )}
                                <span className="line-clamp-2">{bullet}</span>
                              </li>
                            ))}
                          </ul>
                          <a
                            href={listing.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md px-3 py-2 hover:opacity-90 transition-opacity w-fit"
                          >
                            Angebot ansehen
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </article>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex h-11 w-11 [&_svg]:h-5 [&_svg]:w-5" />
                <CarouselNext className="hidden md:flex h-11 w-11 [&_svg]:h-5 [&_svg]:w-5" />
              </Carousel>
            </div>
          )}
        </>
      )}
    </section>
  );
};
